import moment from 'moment'
import {
  CAMPAIGN_STATUSES,
  CAMPAIGN_STATUS_COLORS, CAMPAIGN_STATUS_LABELS,
  CHANNEL_COLORS, CHANNEL_LABELS, CHANNEL_OPTIONS,
  CAMPAIGN_TYPES
} from '@/utils/campaignOptions'

export default {
  computed: {
    startDate () {
      return moment(this.campaign.start_date, 'YYYY-MM-DD').parseZone()
    },
    endDate () {
      return moment(this.campaign.finish_date, 'YYYY-MM-DD').parseZone()
    },
    timeTotal () {
      return this.endDate.diff(this.startDate, 'days') + 1
    },
    timeActive () {
      return moment().diff(this.startDate, 'days')
    },
    timeActiveLeft () {
      return this.endDate.diff(moment(), 'days')
    },
    timeLeft () {
      return this.startDate.diff(moment(), 'days')
    },
    getWidth () {
      return this.isActive ? `${this.timeActive / this.timeTotal * 100}%` : '100%'
    },
    getCompactChannelInfo () {
      return this.campaign.channels.map(c => CHANNEL_LABELS[c.type]).join(', ')
    },
    isConfirmed () {
      const { AGENCY_CONFIRMED, ACTIVE, FINISHED } = CAMPAIGN_STATUSES
      return [AGENCY_CONFIRMED, ACTIVE, FINISHED].indexOf(this.campaign.status) !== -1
    },
    isFinished () {
      return this.campaign.status === CAMPAIGN_STATUSES.FINISHED || this.timeActiveLeft < 0
    },
    isActive () {
      return this.timeActiveLeft >= 0
    },
    isNotYetActive () {
      return this.timeLeft > 0
    },
    metadataStatus () {
      return this.campaign.metadata && this.campaign.metadata.exterior_director_status
    },
    metadataStatusLabel () {
      return CAMPAIGN_STATUS_LABELS[this.metadataStatus]
    },
    metadataComment () {
      return this.campaign.metadata && this.campaign.metadata.exterior_director_comment
    },
    isExterior () {
      return this.campaign.type === CAMPAIGN_TYPES.EXTERIOR
    },
    status () {
      if (this.isExterior) {
        return [CAMPAIGN_STATUSES.CENTRAL_READY, CAMPAIGN_STATUSES.CENTRAL_ACCEPTED, CAMPAIGN_STATUSES.CENTRAL_REJECTED].indexOf(this.campaign.status) === -1
          ? this.campaign.status
          : this.lastCommentStatus(this.campaign.status)
      }
      return this.campaign.status
    },
    statusColor () {
      return CAMPAIGN_STATUS_COLORS[this.status]
    },
    statusLabel () {
      return CAMPAIGN_STATUS_LABELS[this.status]
    },
    statusTagClass () {
      const statuses = [CAMPAIGN_STATUSES.AGENCY_CONFIRMED, CAMPAIGN_STATUSES.CENTRAL_READY, CAMPAIGN_STATUSES.DIRECTOR_ACCEPTED, CAMPAIGN_STATUSES.DIRECTOR_REJECTED]
      const green = this.status === statuses[2] ? 'green' : ''
      const red = this.status === statuses[3] ? 'red' : ''
      return statuses.indexOf(this.status) === -1 ? '' : `positive ${green} ${red}`
    },
    channelsWithOptions () {
      return this.campaign.channels.map(c => {
        const label = CHANNEL_LABELS[c.type]
        const color = CHANNEL_COLORS[c.type]
        return { ...c, label, color }
      })
    },
    hasCreativities () {
      return this.creativities.length > 0
    },
    hasManyCreativities () {
      return this.creativities.length > 1
    },
    creativities () {
      return this.campaign.channels
        .filter(c => c.creativities && c.type !== CHANNEL_OPTIONS.GOOGLE)
        .flatMap(c => {
          const label = CHANNEL_LABELS[c.type]
          const files = Array.isArray(c.creativities) ? c.creativities : [c.creativities]
          return files.map(f => ({
            url: f,
            has_link: typeof f === 'string' && f.startsWith('http'),
            name: label,
            loading: false
          }))
        })
    }
  },
  methods: {
    lastCommentStatus () {
      const commentsFromDirector = []
      const commentsFromCentral = []
      const flowDate = this.campaign.metadata && this.campaign.metadata.flow_date ? new Date(new Date(this.campaign.metadata.flow_date).toISOString()) : null
      let lastDirectorComment = null
      let lastCentralComment = null

      if (this.campaign.metadata && this.campaign.metadata.exterior_director_data) {
        this.campaign.metadata.exterior_director_data.forEach(comment => {
          commentsFromDirector.push({ status: comment.status, date: new Date(new Date(comment.date).toISOString()) })
        })
      }

      if (this.campaign.status_data) {
        this.campaign.status_data.forEach(comment => {
          commentsFromCentral.push({ status: comment.status, date: new Date(new Date(comment.date).toISOString()) })
        })
      }

      if (commentsFromCentral.length > 0) {
        commentsFromCentral.sort((a, b) => {
          return b.date - a.date
        })
      }

      if (commentsFromDirector.length > 0) {
        commentsFromDirector.sort((a, b) => {
          return b.date - a.date
        })
      }

      if (commentsFromDirector.length > 0) {
        lastDirectorComment = commentsFromDirector[0]
      }

      if (commentsFromCentral.length > 1) {
        lastCentralComment = commentsFromCentral[0]
      }

      if (!flowDate) {
        if (lastDirectorComment && lastCentralComment) {
          if (lastDirectorComment.date > lastCentralComment.date) {
            return lastDirectorComment.status
          } else if (lastCentralComment.status !== CAMPAIGN_STATUSES.DRAFT) {
            return lastCentralComment.status
          } else {
            return CAMPAIGN_STATUSES.CENTRAL_READY
          }
        } else if (lastDirectorComment && !lastCentralComment) {
          return lastDirectorComment.status
        } else if (lastCentralComment && !lastDirectorComment) {
          return lastCentralComment.status
        }
      } else {
        if (lastDirectorComment && lastCentralComment) {
          if (lastDirectorComment.date > lastCentralComment.date) {
            if (lastDirectorComment.date > flowDate) {
              return lastDirectorComment.status
            } else {
              return CAMPAIGN_STATUSES.CENTRAL_READY
            }
          } else if (lastCentralComment.status !== CAMPAIGN_STATUSES.DRAFT) {
            if (lastCentralComment.date > flowDate) {
              return lastCentralComment.status
            } else {
              return CAMPAIGN_STATUSES.CENTRAL_READY
            }
          } else {
            return CAMPAIGN_STATUSES.CENTRAL_READY
          }
        } else if (lastDirectorComment && !lastCentralComment) {
          return lastDirectorComment.status
        } else if (lastCentralComment && !lastDirectorComment) {
          return lastCentralComment.status
        }
      }

      return CAMPAIGN_STATUSES.CENTRAL_READY
    }
  }
}
